import {
  AspectRatio,
  BoxProps,
  Card,
  CardBody,
  CardHeader,
  HStack,
  Heading,
  Icon,
  Link,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { PlayIcon } from '@heroicons/react/outline';
import { ExclamationCircleIcon } from '@heroicons/react/solid';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { Exceptions } from '@blockpulse3/data/shared';
import {
  CompanyDocumentType,
  DocumentStatus,
  FundraisingDocumentType,
  IdentityType,
  IdentityVerificationStatus,
  useGenerateSubscriptionIbanMutation,
  useGetDocumentPdfUrlLazyQuery,
  useGetSubscriptionFundraisingDocumentsQuery,
} from '@blockpulse3/graphql/hooks';
import { getDocumentIds } from '@blockpulse3/helpers';
import {
  DocumentLink,
  ErrorQueryCard,
  IdentityAvatar,
  IdentityCard,
  IdentityCardDescription,
  IdentityCardTitle,
  ResponsiveModal,
} from '@blockpulse3/ui/commons';

type DocumentTypeMessage = keyof Messages['DocumentValues'];
type Props = BoxProps;

/**
 * SubscriptionDocumentList.
 * View of all subscription related documents.
 *
 * @returns {JSX.Element}
 */
export function SubscriptionDocumentList({ ...props }: Props): JSX.Element {
  const i18nSubscriptionDocumentsList = useTranslations('SubscriptionDocumentsList');
  const i18nDocumentTitle = useTranslations('DocumentValues');

  const { subscriptionId = '' } = useParams();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [isBankDetailsGenerated, setIsBankDetailsGenerated] = useState<boolean>(false);
  const [isBankDetailsGenerating, setIsBankDetailsGenerating] = useState<boolean>(false);

  const [generateSubscriptionIban, { error: generateError }] =
    useGenerateSubscriptionIbanMutation();

  const { data, loading, error, refetch } = useGetSubscriptionFundraisingDocumentsQuery({
    variables: { subscriptionId },
  });

  const [getDocumentPdfUrl] = useGetDocumentPdfUrlLazyQuery();

  const reference = data?.subscription?.reference?.id;

  useEffect(() => {
    if (!data) return;
    if (!reference && !isBankDetailsGenerated && !isBankDetailsGenerating) {
      setIsBankDetailsGenerating(true);
      generateSubscriptionIban({
        variables: {
          subscriptionId: data.subscription.id,
        },
        onCompleted: () => {
          setIsBankDetailsGenerated(true);
          setIsBankDetailsGenerating(false);
          refetch();
        },
        onError: () => {
          setIsBankDetailsGenerated(true);
          setIsBankDetailsGenerating(false);
        },
      });
    } else {
      setIsBankDetailsGenerated(true);
    }
  }, [
    reference,
    data,
    isBankDetailsGenerated,
    generateSubscriptionIban,
    isBankDetailsGenerating,
    refetch,
  ]);

  if (loading || !isBankDetailsGenerated) {
    return (
      <Skeleton>
        <Card h="220px" />
      </Skeleton>
    );
  }

  if (error || !data) {
    return <ErrorQueryCard />;
  }

  const { subscription } = data;
  const { identity, fundraising } = subscription;
  const { name: companyName, signer, contact } = fundraising.company;

  const repIndividualIdentity = contact?.individualIdentity || signer.individualIdentity;
  const isRepVerified =
    repIndividualIdentity?.identity?.verificationStatus === IdentityVerificationStatus.APPROVED;

  const documentIds = getDocumentIds(subscription.subscriptionDocuments);
  console.log('documentIds', documentIds);
  const holdingMethodCertificate = subscription.subscriptionDocuments.find(
    (doc) =>
      doc.status === DocumentStatus.SIGNED &&
      [
        FundraisingDocumentType.IR_PME_CERTIFICATE,
        FundraisingDocumentType.PEA_CERTIFICATE,
        FundraisingDocumentType.PEA_PME_CERTIFICATE,
        FundraisingDocumentType.OWNERSHIP_CERTIFICATE,
      ].includes(doc.type as FundraisingDocumentType),
  );
  const customFile = subscription.subscriptionDocuments.filter(
    (doc) => doc.type === FundraisingDocumentType.CUSTOM_FILE,
  );

  const onDocumentClick = (documentId: string): void => {
    getDocumentPdfUrl({
      variables: {
        documentId,
      },
      fetchPolicy: 'no-cache',
      onCompleted: ({ getDocumentPdfUrl: pdfUrl }) => {
        window.open(pdfUrl, '_blank');
      },
    });
  };

  const showBankDetailsDocument =
    identity.isVerified &&
    !subscription?.bankVirtualIban?.deletedAt &&
    !!documentIds[FundraisingDocumentType.IBAN];

  return (
    <Card {...props}>
      <CardHeader>
        <Heading size="md">
          {i18nSubscriptionDocumentsList('DocumentHeader', {
            companyName,
          })}
        </Heading>
      </CardHeader>
      <CardBody>
        <Stack spacing="4">
          <Stack spacing="2">
            <DocumentLink
              fileName={i18nSubscriptionDocumentsList('STATUTS', {
                companyName,
              })}
              onClick={(): void => onDocumentClick(documentIds[CompanyDocumentType.STATUTS])}
            />
            <DocumentLink
              fileName={i18nSubscriptionDocumentsList('REGISTER_EXTRACT', {
                companyName,
              })}
              onClick={(): void =>
                onDocumentClick(documentIds[CompanyDocumentType.REGISTER_EXTRACT])
              }
            />
            <DocumentLink
              fileName={i18nSubscriptionDocumentsList('PV_EXERCICE')}
              onClick={(): void =>
                onDocumentClick(documentIds[FundraisingDocumentType.PV_EXERCICE])
              }
            />
            {showBankDetailsDocument && (
              <DocumentLink
                fileName={i18nSubscriptionDocumentsList('IBAN')}
                onClick={(): void => onDocumentClick(documentIds[FundraisingDocumentType.IBAN])}
              />
            )}
            {generateError && generateError.message !== Exceptions.IbanGenerationRejected && (
              <HStack>
                <Icon
                  as={ExclamationCircleIcon}
                  backgroundColor="transparent"
                  borderRadius="100px"
                  boxSize="30px"
                  color="red.500"
                  p="4px"
                />
                <Text color="red.500" fontSize="sm">
                  {i18nSubscriptionDocumentsList('IbanGenerationFailed')}
                </Text>
              </HStack>
            )}
            {holdingMethodCertificate && (
              <DocumentLink
                fileName={i18nDocumentTitle(
                  `Document${holdingMethodCertificate.type}` as DocumentTypeMessage,
                )}
                onClick={(): void => onDocumentClick(holdingMethodCertificate.id)}
              />
            )}
            {customFile.map((file, index) => {
              const fileName =
                file.title === 'CUSTOM_FILE'
                  ? `${i18nSubscriptionDocumentsList('CUSTOM_FILE')} ${++index}`
                  : file.title;
              return (
                <DocumentLink
                  key={file.id}
                  fileName={fileName}
                  onClick={(): void => onDocumentClick(file.id)}
                />
              );
            })}
            {data.subscription.fundraising.videoLink && (
              <HStack>
                <Icon
                  as={PlayIcon}
                  backgroundColor="gray.50"
                  borderRadius="100px"
                  boxSize="30px"
                  color="gray.500"
                  p="4px"
                />
                <Link onClick={onOpen}>
                  <Text fontSize="sm" textDecoration="underline">
                    {i18nSubscriptionDocumentsList('Video')}
                  </Text>
                </Link>
                <ResponsiveModal
                  isOpen={isOpen}
                  scrollBehavior="outside"
                  size="3xl"
                  onClose={onClose}
                >
                  <ModalOverlay />
                  <ModalContent>
                    <ModalHeader>{i18nSubscriptionDocumentsList('Video')}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                      <AspectRatio ratio={16 / 9}>
                        <iframe
                          allowFullScreen
                          src={subscription.fundraising.videoLink || ''}
                          title="Subscription Video"
                        />
                      </AspectRatio>
                    </ModalBody>
                  </ModalContent>
                </ResponsiveModal>
              </HStack>
            )}
          </Stack>

          <Heading color="gray.600" fontSize="md">
            {i18nSubscriptionDocumentsList(contact?.individualIdentity ? 'Contact' : 'President')}
          </Heading>
          <Stack bgColor="gray.50" borderRadius="2" padding="3">
            <IdentityCard>
              <IdentityAvatar
                boxSize="10"
                src={repIndividualIdentity?.identity?.profilePicture}
                type={IdentityType.INDIVIDUAL}
              />
              <Stack spacing="0">
                <IdentityCardTitle isChecked={isRepVerified}>
                  <Text>{repIndividualIdentity?.name}</Text>
                </IdentityCardTitle>
                <IdentityCardDescription>
                  <Text>{repIndividualIdentity?.email}</Text>
                </IdentityCardDescription>
              </Stack>
            </IdentityCard>
          </Stack>
        </Stack>
      </CardBody>
    </Card>
  );
}

export type SubscriptionDocumentListProps = Props;
